import React, { useEffect, useMemo, useState } from "react";
import { db, auth, storage } from "../firebase";
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useBuilding } from "../context/BuildingContext";
import { useEffectiveIdentity, useIsReadOnly } from "../context/EffectiveIdentityContext";
import { useToast } from "../components/Toast";
import { leerFichaResidente } from "../utils/fichaResidente";
import { leerPropiasDelEdificio, useridsDeLaCuenta } from "../utils/consultasPropias";
import { getAllActiveFunds } from "../utils/fundHelpers";
import { formatCurrencyUY, formatDateLocalized } from "../utils/formatters";
import { useIsMobile } from "../hooks/useResponsive";
import { useTranslation } from "react-i18next";
import {
  AppScreen,
  AppHeader,
  AppCard,
  SegmentedControl,
  AppButton,
  Badge,
  AppEmptyState,
  AppSpinner,
  IconBox,
} from "../components/app";
import { appColors, appType, appRadius, appSpacing } from "../utils/appTheme";
import { colors, spacing, radius, shadows, typography, pageContainer } from "../utils/designTokens";
import {
  FaHandHoldingUsd,
  FaCheckCircle,
  FaTimesCircle,
  FaReceipt,
  FaFileUpload,
  FaClock,
} from "react-icons/fa";

/**
 * Gastos del edificio cargados por vecinos (colección `expenseclaims`).
 *
 * Un residente carga un gasto que pagó de su bolsillo (con la factura) → otro
 * PROPIETARIO lo aprueba → una Cloud Function (Admin SDK) materializa el gasto
 * del edificio + un crédito (reintegro) que el próximo cierre le descuenta de sus
 * gastos comunes. El admin puede rechazar/revertir/cambiar el fondo desde su panel.
 *
 * Tres vistas (Cargar / Para aprobar / Mis gastos). Layout responsive:
 *   - Mobile: primitivas de `components/app` (peso 400).
 *   - Desktop: chrome de escritorio (designTokens), form en 2 columnas y listas en
 *     grilla que aprovechan el ancho (mismo criterio que CuentasEdificio).
 * i18n ES/EN/pt-BR.
 */

const STATUS_TONE = { pending: "amber", approved: "green", rejected: "red" };
const DSTATUS = {
  pending: { bg: colors.warningLight, fg: colors.warningDark },
  approved: { bg: colors.successLight, fg: colors.successDark },
  rejected: { bg: colors.errorLight, fg: colors.errorDark },
};

const GastosVecinos = ({ setSelected, goBack }) => {
  const { t, i18n } = useTranslation("gastosVecinos");
  const isMobile = useIsMobile();
  const { showToast } = useToast();
  const { selectedBuilding, userUnitsInBuilding } = useBuilding();
  const { email: effEmail, userid: effUserid, viewingAs } = useEffectiveIdentity();
  // "Ver como residente" (quien mira es staff): solo lectura.
  const viendoComo = useIsReadOnly();
  // Edificio de solo lectura (ex-ocupante): las reglas no le dejan cargar,
  // aprobar ni rechazar, y solo lee los gastos que cargó él (C12).
  const exOcupante = !!selectedBuilding?.isReadOnly;
  const readOnly = viendoComo || exOcupante;
  const buildingId = selectedBuilding?.buildingid;

  // El residente es propietario si figura como owner en alguna unidad del edificio.
  const isOwner = (userUnitsInBuilding || []).some((u) => u.userRole === "Propietario");

  const [tab, setTab] = useState("cargar");
  const [loading, setLoading] = useState(true);
  const [claims, setClaims] = useState([]);
  const [identity, setIdentity] = useState({ userid: null, name: "", unit: null });

  // Formulario de carga
  const [concept, setConcept] = useState("");
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("UYU");
  const [fund, setFund] = useState("caja");
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Modal de rechazo
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [acting, setActing] = useState(false);

  const funds = useMemo(() => getAllActiveFunds(selectedBuilding), [selectedBuilding]);
  const fundName = (id) => funds.find((f) => f.id === id)?.name || id;

  // Resolver identidad (userid app + unidad) del usuario efectivo. En "ver
  // como" sale del contexto: quien mira es staff y con C10 no lista users por
  // email.
  useEffect(() => {
    const resolve = async () => {
      if (!effEmail) return;
      try {
        const ficha = await leerFichaResidente(db, {
          email: effEmail,
          userid: effUserid,
          viendoComo,
          nombre: viewingAs?.name,
        });
        const userid = ficha ? ficha.userid : auth.currentUser?.uid;
        const name = ficha?.data.NombreCompleto || ficha?.data.name || effEmail;
        const units = userUnitsInBuilding || [];
        const unit = units.find((u) => u.userRole === "Propietario") || units[0] || null;
        setIdentity({ userid: userid != null ? String(userid) : null, name, unit });
      } catch (e) {
        console.error("GastosVecinos: no se pudo resolver identidad", e);
      }
    };
    resolve();
  }, [effEmail, effUserid, viendoComo, viewingAs?.name, userUnitsInBuilding]);

  // Gastos de vecinos del edificio. El vigente (y el staff en "ver como") lee
  // todos; el ex-ocupante solo los que cargó él (C12): su consulta fija también
  // el userid, con los de su cuenta (user_access) o, en "ver como", el del
  // residente mirado.
  const loadClaims = async () => {
    if (!buildingId) return;
    setLoading(true);
    try {
      const uidSesion = viendoComo ? null : auth.currentUser?.uid || null;
      const all = await leerPropiasDelEdificio(db, "expenseclaims", buildingId, {
        soloPropias: exOcupante,
        propios: async () => {
          const ids = await useridsDeLaCuenta(db, { uid: uidSesion, respaldo: [effUserid] });
          if (ids.length > 0) return ids;
          // Sin user_access todavía: el userid de la ficha que muestra la pantalla.
          const ficha = await leerFichaResidente(db, {
            email: effEmail,
            userid: effUserid,
            viendoComo,
            nombre: viewingAs?.name,
          });
          return ficha ? [ficha.userid] : [];
        },
      });
      all.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setClaims(all);
    } catch (e) {
      console.error("GastosVecinos: error cargando claims", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadClaims();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildingId, exOcupante, viendoComo, effUserid, effEmail]);

  // Al ex-ocupante la regla ya le devuelve solo los suyos (también los de otra
  // ficha de su cuenta); al vigente, los del edificio filtrados por su userid.
  const myClaims = useMemo(
    () =>
      exOcupante
        ? claims
        : claims.filter((c) => identity.userid && String(c.userid) === identity.userid),
    [claims, identity.userid, exOcupante]
  );
  const toApprove = useMemo(
    () =>
      claims.filter(
        (c) =>
          c.status === "pending" &&
          c.createdByUid !== auth.currentUser?.uid &&
          (!identity.userid || String(c.userid) !== identity.userid)
      ),
    [claims, identity.userid]
  );

  const handleFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) return showToast(t("toast.fileTooLarge"), "error");
    const allowed = ["image/jpeg", "image/png", "image/webp", "application/pdf"];
    if (!allowed.includes(f.type)) return showToast(t("toast.fileType"), "error");
    setFile(f);
  };

  const handleSubmit = async () => {
    if (readOnly) return showToast(t("toast.readOnly"));
    const amt = Number(amount);
    if (!concept.trim()) return showToast(t("toast.needConcept"), "error");
    if (!amt || amt <= 0) return showToast(t("toast.needAmount"), "error");
    if (!file) return showToast(t("toast.needFile"), "error");
    if (!identity.userid || !auth.currentUser?.uid) return showToast(t("toast.noIdentity"), "error");
    setSubmitting(true);
    try {
      const ext = file.name.split(".").pop();
      const path = `expenseclaims/${buildingId}/${Date.now()}_${Math.random().toString(36).slice(2, 8)}.${ext}`;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, file);
      const invoiceUrl = await getDownloadURL(storageRef);

      await addDoc(collection(db, "expenseclaims"), {
        buildingid: Number(buildingId),
        createdByUid: auth.currentUser.uid,
        userid: identity.userid,
        userEmail: effEmail,
        userName: identity.name || "",
        unitId: identity.unit?.id || null,
        unitNumber: identity.unit?.number ?? null,
        concept: concept.trim(),
        amount: amt,
        currency,
        sourceFund: fund,
        invoiceUrl,
        invoiceName: file.name,
        status: "pending",
        createdAt: serverTimestamp(),
      });

      showToast(t("toast.submitted"), "success");
      setConcept("");
      setAmount("");
      setFund("caja");
      setCurrency("UYU");
      setFile(null);
      setTab("mios");
      await loadClaims();
    } catch (e) {
      console.error("GastosVecinos: error creando claim", e);
      showToast(t("toast.submitError"), "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async (claim) => {
    if (readOnly) return showToast(t("toast.readOnly"));
    setActing(true);
    try {
      await updateDoc(doc(db, "expenseclaims", claim.id), {
        status: "approved",
        approvedByUid: auth.currentUser?.uid || null,
        approvedByEmail: effEmail,
        approvedByName: identity.name || effEmail,
        approvedAt: serverTimestamp(),
      });
      showToast(t("toast.approved"), "success");
      await loadClaims();
    } catch (e) {
      console.error("GastosVecinos: error aprobando", e);
      showToast(t("toast.actionError"), "error");
    } finally {
      setActing(false);
    }
  };

  const handleReject = async () => {
    if (!rejectModal) return;
    setActing(true);
    try {
      await updateDoc(doc(db, "expenseclaims", rejectModal.id), {
        status: "rejected",
        rejectionReason: rejectReason.trim() || null,
        rejectedByEmail: effEmail,
        rejectedByName: identity.name || effEmail,
        rejectedAt: serverTimestamp(),
      });
      showToast(t("toast.rejected"), "success");
      setRejectModal(null);
      setRejectReason("");
      await loadClaims();
    } catch (e) {
      console.error("GastosVecinos: error rechazando", e);
      showToast(t("toast.actionError"), "error");
    } finally {
      setActing(false);
    }
  };

  const locale = i18n.language;
  const money = (c) => `${c.currency === "USD" ? "US$ " : ""}${formatCurrencyUY(Number(c.amount || 0))}`;
  const dateOf = (c) =>
    c.createdAt?.seconds ? formatDateLocalized(new Date(c.createdAt.seconds * 1000), locale) : "";

  const effectiveTab = tab === "aprobar" && !isOwner ? "cargar" : tab;

  const tabOptions = useMemo(() => {
    const opts = [{ value: "cargar", label: t("tabs.submit") }];
    if (isOwner)
      opts.push({
        value: "aprobar",
        label: toApprove.length ? `${t("tabs.approve")} (${toApprove.length})` : t("tabs.approve"),
      });
    opts.push({ value: "mios", label: t("tabs.mine") });
    return opts;
  }, [isOwner, toApprove.length, t]);

  // ── Campos del formulario (compartidos mobile/desktop) ────────────────────
  const renderFormFields = (desktop) => {
    const iStyle = desktop ? dInputStyle : inputStyle;
    const fStyle = desktop ? dFileBtnStyle : fileBtnStyle;
    const labelStyle = desktop
      ? { fontSize: 13, color: colors.gray700, fontWeight: 400 }
      : { fontSize: 12.5, color: appColors.ink2, fontWeight: 400 };
    const hintColor = desktop ? colors.gray400 : appColors.ink3;
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: desktop ? 18 : 16 }}>
        <Field label={t("submit.concept")} labelStyle={labelStyle}>
          <input
            type="text"
            value={concept}
            onChange={(e) => setConcept(e.target.value)}
            placeholder={t("submit.conceptPlaceholder")}
            style={iStyle}
            aria-label={t("submit.concept")}
          />
        </Field>

        <div style={{ display: "flex", gap: 12 }}>
          <Field label={t("submit.amount")} labelStyle={labelStyle} style={{ flex: 1 }}>
            <input
              type="number"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0"
              style={iStyle}
              aria-label={t("submit.amount")}
            />
          </Field>
          <Field label={t("submit.currency")} labelStyle={labelStyle} style={{ width: 140 }}>
            <SegmentedControl
              options={[
                { value: "UYU", label: "$" },
                { value: "USD", label: "US$" },
              ]}
              value={currency}
              onChange={setCurrency}
            />
          </Field>
        </div>

        <Field label={t("submit.fund")} labelStyle={labelStyle}>
          <SegmentedControl
            options={funds.map((f) => ({ value: f.id, label: f.name }))}
            value={fund}
            onChange={setFund}
          />
          <div style={{ fontSize: 12, color: hintColor, marginTop: 6 }}>{t("submit.fundHint")}</div>
        </Field>

        <Field label={t("submit.invoice")} labelStyle={labelStyle}>
          <label style={fStyle}>
            <FaFileUpload />
            <span>{file ? file.name : t("submit.pickFile")}</span>
            <input type="file" accept="image/*,application/pdf" onChange={handleFile} style={{ display: "none" }} />
          </label>
        </Field>

        <AppButton variant="primary" block icon={<FaHandHoldingUsd />} disabled={submitting || readOnly} onClick={handleSubmit}>
          {submitting ? t("submit.sending") : t("submit.send")}
        </AppButton>
      </div>
    );
  };

  // ── MOBILE ────────────────────────────────────────────────────────────────
  const renderClaimCard = (c, { withActions = false } = {}) => (
    <AppCard key={c.id} style={{ marginBottom: appSpacing.md }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
        <IconBox icon={<FaReceipt />} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "baseline" }}>
            <span style={{ fontSize: 15, color: appColors.ink, fontWeight: 400 }}>{c.concept}</span>
            <span style={{ fontSize: 15, color: appColors.ink, fontWeight: 400, whiteSpace: "nowrap" }}>{money(c)}</span>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginTop: 8 }}>
            <Badge tone={STATUS_TONE[c.status] || "ink3"} dot>
              {t(`status.${c.status}`)}
            </Badge>
            <span style={{ fontSize: 12.5, color: appColors.ink3 }}>{t("card.fund")}: {fundName(c.sourceFund)}</span>
            {c.userName && <span style={{ fontSize: 12.5, color: appColors.ink3 }}>· {c.userName}</span>}
            {dateOf(c) && <span style={{ fontSize: 12.5, color: appColors.ink3 }}>· {dateOf(c)}</span>}
          </div>
          {c.status === "rejected" && c.rejectionReason && (
            <div style={{ fontSize: 12.5, color: appColors.red, marginTop: 8 }}>{t("card.reason")}: {c.rejectionReason}</div>
          )}
          {c.invoiceUrl && (
            <a href={c.invoiceUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12.5, color: appColors.blue, marginTop: 8, display: "inline-block" }}>
              {t("card.viewInvoice")}
            </a>
          )}
          {withActions && (
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <AppButton variant="primary" icon={<FaCheckCircle />} disabled={acting || readOnly} onClick={() => handleApprove(c)} style={{ flex: 1 }}>
                {t("actions.approve")}
              </AppButton>
              <AppButton variant="outline" icon={<FaTimesCircle />} disabled={acting || readOnly} onClick={() => { setRejectModal(c); setRejectReason(""); }} style={{ flex: 1 }}>
                {t("actions.reject")}
              </AppButton>
            </div>
          )}
        </div>
      </div>
    </AppCard>
  );

  if (isMobile) {
    return (
      <AppScreen
        header={<AppHeader title={t("header.title")} subtitle={selectedBuilding?.name || t("header.subtitle")} onBack={goBack} />}
      >
        <div style={{ width: "100%" }}>
          <SegmentedControl options={tabOptions} value={effectiveTab} onChange={setTab} style={{ marginBottom: appSpacing.md }} />

          {effectiveTab === "cargar" && (
            <AppCard>
              <div style={{ fontSize: 13, color: appColors.ink2, lineHeight: 1.5, marginBottom: 16 }}>{t("submit.intro")}</div>
              {renderFormFields(false)}
            </AppCard>
          )}

          {effectiveTab === "aprobar" && isOwner && (
            loading ? <AppSpinner /> : toApprove.length === 0 ? (
              <AppEmptyState icon={<FaClock />} title={t("empty.approve")} />
            ) : toApprove.map((c) => renderClaimCard(c, { withActions: true }))
          )}

          {effectiveTab === "mios" && (
            loading ? <AppSpinner /> : myClaims.length === 0 ? (
              <AppEmptyState
                icon={<FaHandHoldingUsd />}
                title={t("empty.mine")}
                action={<AppButton variant="outline" onClick={() => setTab("cargar")}>{t("empty.mineAction")}</AppButton>}
              />
            ) : myClaims.map((c) => renderClaimCard(c))
          )}
        </div>
        {renderRejectModal()}
      </AppScreen>
    );
  }

  // ── DESKTOP ─────────────────────────────────────────────────────────────
  const DClaimCard = ({ c, withActions }) => {
    const s = DSTATUS[c.status] || { bg: colors.gray100, fg: colors.gray700 };
    return (
      <div style={{ background: colors.bgCard, borderRadius: radius.md, boxShadow: shadows.sm, padding: 18, display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 15, color: colors.black, marginBottom: 2 }}>{c.concept}</div>
            <div style={{ fontSize: 12.5, color: colors.gray400 }}>
              {c.userName || ""}{c.unitNumber ? ` · Unidad ${c.unitNumber}` : ""}{dateOf(c) ? ` · ${dateOf(c)}` : ""}
            </div>
          </div>
          <div style={{ fontSize: 17, color: colors.black, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>{money(c)}</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 10px", borderRadius: radius.full, background: s.bg, color: s.fg, fontSize: 12.5 }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor" }} />
            {t(`status.${c.status}`)}
          </span>
          <span style={{ fontSize: 12.5, color: colors.gray600 }}>{t("card.fund")}: {fundName(c.sourceFund)}</span>
          {c.invoiceUrl && (
            <a href={c.invoiceUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12.5, color: colors.primary, marginLeft: "auto" }}>
              {t("card.viewInvoice")}
            </a>
          )}
        </div>
        {c.status === "rejected" && c.rejectionReason && (
          <div style={{ fontSize: 12.5, color: colors.error }}>{t("card.reason")}: {c.rejectionReason}</div>
        )}
        {withActions && (
          <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
            <button style={{ ...dBtn(colors.primary, true), flex: 1 }} disabled={acting || readOnly} onClick={() => handleApprove(c)}>
              <FaCheckCircle size={13} /> {t("actions.approve")}
            </button>
            <button style={{ ...dBtn(colors.error, false), flex: 1 }} disabled={acting || readOnly} onClick={() => { setRejectModal(c); setRejectReason(""); }}>
              <FaTimesCircle size={13} /> {t("actions.reject")}
            </button>
          </div>
        )}
      </div>
    );
  };

  const dGrid = (items, withActions) => (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: spacing[4] }}>
      {items.map((c) => <DClaimCard key={c.id} c={c} withActions={withActions} />)}
    </div>
  );

  return (
    <div style={pageContainer}>
      {/* Header (mismo patrón que MisPagos/Expensas) */}
      <div style={dStyles.headerCard}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={dStyles.headerIcon}>
            <FaHandHoldingUsd />
          </div>
          <div style={{ flex: 1 }}>
            <h1 style={dStyles.title}>{t("header.title")}</h1>
            <p style={dStyles.subtitle}>{selectedBuilding?.name || t("header.subtitleDesktop")}</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "inline-flex", gap: 6, background: colors.bgCard, border: `1px solid ${colors.gray200}`, borderRadius: radius.full, padding: 4, alignSelf: "flex-start", marginBottom: spacing[5] }}>
        {tabOptions.map((o) => {
          const active = o.value === effectiveTab;
          return (
            <button
              key={o.value}
              onClick={() => setTab(o.value)}
              style={{
                border: "none",
                borderRadius: radius.full,
                padding: "9px 20px",
                cursor: "pointer",
                fontFamily: "inherit",
                fontSize: 14,
                fontWeight: 400,
                background: active ? colors.primary : "transparent",
                color: active ? "#fff" : colors.gray700,
                transition: "background .15s ease",
              }}
            >
              {o.label}
            </button>
          );
        })}
      </div>

      {/* CARGAR: form (izq) + panel informativo (der) */}
      {effectiveTab === "cargar" && (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.35fr) minmax(0, 1fr)", gap: spacing[5], alignItems: "start", maxWidth: 1040 }}>
          <div style={{ background: colors.bgCard, borderRadius: radius.md, boxShadow: shadows.sm, padding: spacing[6] }}>
            {renderFormFields(true)}
          </div>
          <div style={{ background: colors.primarySubtle, borderRadius: radius.md, padding: spacing[6], display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={{ fontSize: typography.sizes?.lg || 16, color: colors.black }}>{t("submit.infoTitle")}</div>
            <div style={{ fontSize: 13.5, color: colors.gray700, lineHeight: 1.55 }}>{t("submit.intro")}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 4 }}>
              {(t("submit.steps", { returnObjects: true }) || []).map((step, i) => (
                <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                  <span style={{ width: 22, height: 22, minWidth: 22, borderRadius: "50%", background: colors.primary, color: "#fff", fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center" }}>{i + 1}</span>
                  <span style={{ fontSize: 13.5, color: colors.gray700, lineHeight: 1.5 }}>{step}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* PARA APROBAR */}
      {effectiveTab === "aprobar" && isOwner && (
        loading ? (
          <div style={{ padding: "48px 0", textAlign: "center", color: colors.gray400 }}>…</div>
        ) : toApprove.length === 0 ? (
          <DEmpty icon={<FaClock />} title={t("empty.approve")} />
        ) : dGrid(toApprove, true)
      )}

      {/* MIS GASTOS */}
      {effectiveTab === "mios" && (
        loading ? (
          <div style={{ padding: "48px 0", textAlign: "center", color: colors.gray400 }}>…</div>
        ) : myClaims.length === 0 ? (
          <DEmpty
            icon={<FaHandHoldingUsd />}
            title={t("empty.mine")}
            action={<button style={dBtn(colors.primary, true)} onClick={() => setTab("cargar")}>{t("empty.mineAction")}</button>}
          />
        ) : dGrid(myClaims, false)
      )}

      {renderRejectModal()}
    </div>
  );

  function renderRejectModal() {
    if (!rejectModal) return null;
    return (
      <div style={overlayStyle} onClick={() => setRejectModal(null)}>
        <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
          <div style={{ fontSize: 16, color: colors.black, marginBottom: 6 }}>{t("reject.title")}</div>
          <div style={{ fontSize: 13, color: colors.gray600, marginBottom: 14 }}>{rejectModal.concept} · {money(rejectModal)}</div>
          <textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder={t("reject.reasonPlaceholder")}
            rows={3}
            style={{ ...dInputStyle, resize: "vertical" }}
            aria-label={t("reject.reason")}
          />
          <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
            <button style={{ ...dBtn(colors.gray600, false), flex: 1 }} onClick={() => setRejectModal(null)}>{t("reject.cancel")}</button>
            <button style={{ ...dBtn(colors.error, true), flex: 1 }} disabled={acting} onClick={handleReject}>{t("reject.confirm")}</button>
          </div>
        </div>
      </div>
    );
  }
};

const Field = ({ label, labelStyle, children, style }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 7, ...style }}>
    <span style={labelStyle || { fontSize: 12.5, color: appColors.ink2, fontWeight: 400 }}>{label}</span>
    {children}
  </div>
);

const DEmpty = ({ icon, title, action }) => (
  <div style={{ background: colors.bgCard, borderRadius: radius.md, boxShadow: shadows.sm, padding: "56px 24px", display: "flex", flexDirection: "column", alignItems: "center", gap: 14, color: colors.gray400 }}>
    <span style={{ fontSize: 34, color: colors.gray300 }}>{icon}</span>
    <span style={{ fontSize: 15, color: colors.gray600 }}>{title}</span>
    {action}
  </div>
);

// ── estilos mobile (appTheme) ──
const inputStyle = {
  width: "100%", boxSizing: "border-box", padding: "12px 14px", borderRadius: appRadius.card,
  border: `1px solid ${appColors.line}`, background: appColors.surface, fontFamily: appType.fontFamily,
  fontSize: 14.5, fontWeight: 400, color: appColors.ink, outline: "none",
};
const fileBtnStyle = {
  display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", borderRadius: appRadius.card,
  border: `1px dashed ${appColors.line}`, background: appColors.surface2, cursor: "pointer",
  fontFamily: appType.fontFamily, fontSize: 13.5, fontWeight: 400, color: appColors.ink2,
};

// ── estilos desktop (designTokens) — mismo chrome que MisPagos/Expensas ──
const dStyles = {
  headerCard: {
    background: colors.bgCard,
    borderRadius: radius.md,
    padding: spacing[5],
    boxShadow: shadows.sm,
    marginBottom: spacing[6],
  },
  headerIcon: {
    width: 42,
    height: 42,
    borderRadius: 10,
    background: colors.primaryLighter,
    color: colors.primary,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 18,
  },
  title: { fontSize: 20, color: colors.primary, fontWeight: 400, margin: 0 },
  subtitle: { margin: "4px 0 0", fontSize: 13, color: colors.gray600 },
};

const dInputStyle = {
  width: "100%", boxSizing: "border-box", padding: "11px 13px", borderRadius: radius.sm,
  border: `1px solid ${colors.gray200}`, background: "#fff", fontFamily: "inherit",
  fontSize: 14, fontWeight: 400, color: colors.black, outline: "none",
};
const dFileBtnStyle = {
  display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", borderRadius: radius.sm,
  border: `1px dashed ${colors.gray300}`, background: colors.gray50, cursor: "pointer",
  fontFamily: "inherit", fontSize: 13.5, fontWeight: 400, color: colors.gray700,
};
const dBtn = (color, filled) => ({
  display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7,
  padding: "10px 16px", borderRadius: radius.sm, cursor: "pointer", fontFamily: "inherit",
  fontSize: 14, fontWeight: 400, border: `1px solid ${color}`,
  background: filled ? color : "#fff", color: filled ? "#fff" : color,
});

const overlayStyle = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,.35)", display: "flex",
  alignItems: "center", justifyContent: "center", padding: 20, zIndex: 1000,
};
const modalStyle = {
  width: "100%", maxWidth: 440, background: "#fff", borderRadius: radius.lg,
  boxShadow: shadows.lg, padding: 24, fontFamily: "'FT Habit', -apple-system, sans-serif",
};

export default GastosVecinos;
